import React, { ReactElement, ReactNode } from 'react';
import {
  AvDateRangePicker,
  DateRangeValueType,
  Filter,
  FilterStyledPopper,
  isDateRangeEmpty,
  isDateRangeNotEmpty,
  Preset,
  SelectTrigger,
  ValueWithPreset,
} from '@fabric-ui/components';
import { AsyncGetOptions } from '@fabric-ui/components/Table';
import { noop } from '@fabric-ui/utils';
import { Box, ClickAwayListener } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FieldFilterModeType, getFieldTypeInfo } from '../utils/filterUtils';
import { generateOption } from '../utils/utils';
import { AliasResolution } from '../views/CustomDashboards/types';
import { typeLabelMap } from '../views/Sources/Mapping/types';
import { FieldFilterTabs } from './filters/components/FieldFilterTabs';
import { useFilterConfig, useFilterContext } from './filters/FilterContext';
import { useFieldFilterDisplay } from './filters/hooks/useFieldFilterDisplay';
import { useFieldFilterOptions } from './filters/hooks/useFieldFilterOptions';
import { useFieldFilterState } from './filters/hooks/useFieldFilterState';
import { useFilterValidation } from './filters/hooks/useFilterValidation';
import { FilterOption } from './filters/types';

export interface FieldFilterBaseProps {
  field: string;
  label?: ReactNode;
  filters?: Record<string, unknown>;
  isEnabled: boolean;
  onChange: (field: string, value: unknown) => void;
  onFilterChange?: (filter: Filter) => void;
  onOpen?: () => void;
  onClose?: () => void;
  setRef?: (ref: HTMLElement | null) => void;
  getOptions?: (data: FilterOption[], label: ReactNode, getValue?: (v: unknown) => ReactNode) => FilterOption[];
  overrideExtra?: string[];
  trigger?: ReactElement;
  fieldType?: keyof typeof typeLabelMap;
  options?: FilterOption[];
  isMultipleCondition?: boolean;
  queryKey?: string;
  getValue?: (v: unknown) => ReactNode;
  isFilterExpression?: boolean;
  usage?: 'SPF' | undefined;
  isGlobalFilter?: boolean;
  activeProjName?: string;
  selectIcon?: ReactNode;
  asyncGetOptions?: AsyncGetOptions;
  extraOperands?: Filter[];
  disablePortalInOperatorSelect?: boolean;
  extraOptions?: string[];
  wrapperClassname?: string;
  /** Cascading: sibling filters to narrow this field's option list by. Opt-in - see AvFilters' `cascadeFilters`. */
  optionsContextFilter?: Filter;
  aliasResolution?: AliasResolution;
  /** Disables only this field's chip (e.g. an inactive global filter), independent of the filter-bar-wide context disabled state. */
  fieldDisabled?: boolean;
  /** Rendered inside the field wrapper, right after the trigger - e.g. an inactive-state warning icon. */
  endAdornment?: ReactNode;
}

const popperOptions = { strategy: 'fixed' } as const;

const fieldsToIncludeMinutesAndHours = new Set(['alert.create_time', 'alert.first_seen']);

export const FieldFilterBase: React.FC<FieldFilterBaseProps> = ({
  onChange,
  onFilterChange,
  field,
  filters = {},
  isEnabled,
  setRef = noop,
  getOptions = (data, label) =>
    data.map(({ value }) =>
      value === null || (value as string)?.length === 0 ? { title: `No ${label}`, value: null } : generateOption(value)
    ),
  label = '',
  overrideExtra,
  trigger,
  fieldType,
  options,
  isMultipleCondition = true,
  onOpen = noop,
  onClose = noop,
  queryKey,
  getValue,
  isFilterExpression = false,
  usage,
  isGlobalFilter = false,
  activeProjName: activeProjNameProp,
  selectIcon,
  asyncGetOptions,
  extraOperands,
  disablePortalInOperatorSelect = true,
  extraOptions,
  wrapperClassname,
  optionsContextFilter,
  aliasResolution,
  fieldDisabled,
  endAdornment,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { disabled, size, activeProjName: contextActiveProjName } = useFilterConfig();
  const isDisabled = disabled || fieldDisabled;
  const { accountEntities } = useFilterContext();
  const { fieldMap = {} } = accountEntities ?? {};
  const activeProjName = activeProjNameProp ?? contextActiveProjName ?? fieldMap[field]?.mainProjId?.name;
  const typeBase =
    fieldType || typeLabelMap[fieldMap[field]?.type as string | number] || (field.toLowerCase().endsWith('count') ? 'number' : 'string');
  const type = `${typeBase}${fieldMap[field]?.repeated ? '[]' : ''}`;
  const isSchemaField = !options;
  const { isBooleanField, isDateField, isNumberField } = getFieldTypeInfo(type);
  const supportedType = !['["MESSAGE"]', 'MESSAGE'].includes(fieldMap?.[field]?.type?.toString());
  const shouldShowMinutesAndHours = isDateField && fieldsToIncludeMinutesAndHours.has(field);

  const state = useFieldFilterState({ field, filters, type, onClose });
  const {
    filterObject,
    advancedFilterValue,
    setAdvancedFilterValue,
    mode,
    setMode,
    value,
    initialFilter,
    conditionValue,
    setConditionValue,
    operator,
    valueOperator,
    buttonRef,
    errors,
    setErrors,
    open,
    setOpen,
    search,
    setSearch,
    debouncedValue,
    escapedValue,
    searchCondition,
    handleSelectConditionChange,
    handleClose,
    handleAdvancedFilterChange,
  } = state;

  const { calcOptions, isLoadingOptions, totalRowCount } = useFieldFilterOptions({
    field,
    filters,
    isEnabled,
    activeProjName,
    mode,
    open,
    isDateField,
    isBooleanField,
    isNumberField,
    isSchemaField,
    supportedType,
    isGlobalFilter,
    value,
    search,
    debouncedValue,
    escapedValue,
    searchCondition,
    options,
    overrideExtra,
    extraOperands,
    extraOptions,
    label,
    getOptions,
    getValue,
    queryKey,
    usage,
    asyncGetOptions,
    optionsContextFilter,
    aliasResolution,
  });

  const { tooltipValue, displayValue } = useFieldFilterDisplay({
    filterObject,
    mode,
    isDateField,
    valueOperator,
    value,
    operator,
    calcOptions,
    conditionValue,
    getValue,
  });

  const getErrors = useFilterValidation(type);

  const handleModeChange = (newMode: FieldFilterModeType) => setMode(newMode);

  const isSelectTrigger = !trigger || (trigger.type as { name?: string }).name === 'Select';

  const triggerProps = {
    ref: buttonRef,
    ...(isSelectTrigger
      ? {
          // An individually-disabled filter keeps the plain disabled background, not the active-filter one.
          hasValidValue:
            !fieldDisabled &&
            Boolean(
              (value as unknown[])?.length || (filterObject?.[valueOperator] as unknown[])?.length || filterObject?.value !== undefined
            ),
          processedValue: value,
          displayValue,
          tooltipValue,
          chipCount: isDateField
            ? undefined
            : filterObject
              ? ((conditionValue?.[operator] as unknown[])?.length ?? 1) - 1
              : (value as unknown[]).length
                ? (value as unknown[]).length - 1
                : undefined,
        }
      : {}),
    open,
    label: label as React.ReactNode,
    size: size as 'small' | 'xSmall' | 'medium',
    disabled: isDisabled,
    selectIcon,
    onClick: () => {
      setOpen(true);
      onOpen();
      if (!supportedType) {
        enqueueSnackbar('Unsupported Filter Type', { variant: 'error' });
      }
    },
  } as const;

  const handleDateChange = (dateValue: ValueWithPreset) => {
    if (dateValue.value === undefined) {
      onChange(field, []);
      return;
    }
    if (isDateRangeEmpty(dateValue.value) || dateValue.value === null) {
      onChange(field, [encodeURIComponent(JSON.stringify({ value: 'empty' }))]);
      return;
    }
    if (isDateRangeNotEmpty(dateValue.value)) {
      onChange(field, [encodeURIComponent(JSON.stringify({ value: 'notEmpty' }))]);
      return;
    }
    onChange(field, [encodeURIComponent(JSON.stringify(dateValue))]);
  };

  return (
    <span key={field} ref={setRef} className={`relative inline-flex${wrapperClassname ? ` ${wrapperClassname}` : ''}`}>
      {trigger ? React.cloneElement(trigger, triggerProps) : <SelectTrigger {...triggerProps} />}
      {endAdornment}
      <FilterStyledPopper
        open={open}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        popperOptions={popperOptions}
        className="w-fit min-w-[400px] p-0">
        <ClickAwayListener onClickAway={handleClose}>
          <Box className={open ? 'block' : 'hidden'}>
            {isDateField ? (
              <AvDateRangePicker
                value={filterObject!.value as DateRangeValueType | null | undefined}
                activePresetValue={filterObject!.preset as Preset | undefined}
                onChange={handleDateChange}
                onClose={handleClose}
                showAsCalendar
                isRange
                showRelativeOptions
                isFilterExpression={isFilterExpression}
                showMinutesAndHours={shouldShowMinutesAndHours}
              />
            ) : (
              <FieldFilterTabs
                field={field}
                type={type}
                mode={mode}
                setMode={handleModeChange}
                isSchemaField={isSchemaField}
                activeProjName={activeProjName}
                initialFilter={initialFilter}
                label={label}
                isLoadingOptions={isLoadingOptions}
                calcOptions={calcOptions}
                value={value as unknown[]}
                totalRowCount={totalRowCount}
                setSearch={setSearch}
                onChange={onChange}
                onFilterChange={onFilterChange}
                filterObject={filterObject}
                conditionValue={conditionValue}
                operator={operator}
                isMultipleCondition={isMultipleCondition}
                disablePortalInOperatorSelect={disablePortalInOperatorSelect}
                handleSelectConditionChange={handleSelectConditionChange}
                setErrors={setErrors}
                setConditionValue={setConditionValue}
                errors={errors}
                advancedFilterValue={advancedFilterValue}
                handleAdvancedFilterChange={handleAdvancedFilterChange}
                setOpen={setOpen}
                getErrors={getErrors}
                setAdvancedFilterValue={setAdvancedFilterValue}
                open={open}
              />
            )}
          </Box>
        </ClickAwayListener>
      </FilterStyledPopper>
    </span>
  );
};

export { findingStateOptions } from './filters/hooks/useFieldFilterState';
